/**
 * Simple Markdown Parser for Tailwind Typography
 * Converts basic markdown syntax to HTML with proper styling
 */

export function parseMarkdown(markdown) {
    let html = markdown;
    
    // Escape HTML characters that aren't part of intentional HTML tags
    html = html.replace(/&/g, '&amp;')
               .replace(/</g, '&lt;')
               .replace(/>/g, '&gt;');
    
    // Restore intentional HTML tags (basic ones)
    html = html.replace(/&lt;(\/?(?:div|span|p|br|strong|em|code|pre|h[1-6]|ul|ol|li|blockquote|a)[^&]*)&gt;/gi, '<$1>');
    
    // Code blocks (must be before inline code)
    html = html.replace(/```(\w+)?\n([\s\S]*?)```/g, (match, lang, code) => {
        const language = lang ? ` data-language="${lang}"` : '';
        return `<pre><code${language}>${code.trim()}</code></pre>`;
    });
    
    // Inline code
    html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
    
    // Headers
    html = html.replace(/^\s*###### (.*$)/gm, '<h6>$1</h6>');
    html = html.replace(/^\s*##### (.*$)/gm, '<h5>$1</h5>');
    html = html.replace(/^\s*#### (.*$)/gm, '<h4>$1</h4>');
    html = html.replace(/^\s*### (.*$)/gm, '<h3>$1</h3>');
    html = html.replace(/^\s*## (.*$)/gm, '<h2>$1</h2>');
    html = html.replace(/^\s*# (.*$)/gm, '<h1>$1</h1>');
    
    // Bold and italic (order matters)
    html = html.replace(/\*\*\*(.*?)\*\*\*/g, '<strong><em>$1</em></strong>');
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
    
    html = html.replace(/___([^_]+)___/g, '<strong><em>$1</em></strong>');
    html = html.replace(/__([^_]+)__/g, '<strong>$1</strong>');
    html = html.replace(/_([^_]+)_/g, '<em>$1</em>');
    
    // Links
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');
    
    // Blockquotes
    html = html.replace(/^\s*> (.*$)/gm, '<blockquote><p>$1</p></blockquote>');
    
    // Horizontal rules
    html = html.replace(/^\s*---+\s*$/gm, '<hr>');
    
    // Lists - handle ordered and unordered
    html = html.replace(/^\s*\d+\. (.*$)/gm, '<li class="ordered">$1</li>');
    html = html.replace(/^\s*[-*+] (.*$)/gm, '<li class="unordered">$1</li>');
    
    // Group consecutive list items into proper lists
    html = html.replace(/((?:<li class="ordered">.*<\/li>\s*)+)/g, '<ol>$1</ol>');
    html = html.replace(/((?:<li class="unordered">.*<\/li>\s*)+)/g, '<ul>$1</ul>');
    
    // Clean up list item classes
    html = html.replace(/<li class="(?:ordered|unordered)">/g, '<li>');
    
    // Paragraphs (split by double newlines, but don't wrap block elements)
    const lines = html.split(/\n\s*\n/);
    html = lines.map(line => {
        line = line.trim();
        if (!line) return '';
        
        // Don't wrap block elements in <p>
        if (line.match(/^<(h[1-6]|blockquote|pre|ul|ol|hr|div)/)) {
            return line;
        }
        
        // Don't wrap if already contains block elements
        if (line.includes('<h') || line.includes('<blockquote') || 
            line.includes('<pre') || line.includes('<ul') || 
            line.includes('<ol') || line.includes('<hr') ||
            line.includes('<div')) {
            return line;
        }
        
        return `<p>${line}</p>`;
    }).join('\n\n');
    
    // Clean up extra whitespace and newlines
    html = html.replace(/\n\s*\n/g, '\n');
    html = html.replace(/^\s+|\s+$/g, '');
    
    return html;
}

/**
 * Renders markdown content into a container with Tailwind Typography styles
 * @param {string} markdown - The markdown content to render
 * @param {string|HTMLElement} container - Container selector or element
 */
export function renderMarkdown(markdown, container) {
    const element = typeof container === 'string' 
        ? document.getElementById(container) || document.querySelector(container)
        : container;
        
    if (!element) {
        console.error(`Container not found:`, container);
        return;
    }
    
    const html = parseMarkdown(markdown);
    element.innerHTML = html;
    
    // Add syntax highlighting classes for code blocks if needed
    const codeBlocks = element.querySelectorAll('pre code[data-language]');
    codeBlocks.forEach(block => {
        block.classList.add('language-' + block.dataset.language);
    });
}

/**
 * Check if text contains markdown patterns
 * @param {string} text 
 * @returns {boolean}
 */
export function containsMarkdown(text) {
    const markdownPatterns = [
        /^\s*#{1,6}\s+/m,     // Headers
        /\*\*.*?\*\*/,        // Bold with asterisks
        /(?<!\*)\*(?!\*).*?(?<!\*)\*(?!\*)/,  // Italic with asterisks (not bold)
        /__.*?__/,            // Bold with underscores
        /(?<!_)_(?!_).*?(?<!_)_(?!_)/,        // Italic with underscores (not bold)
        /`[^`]+`/,            // Inline code
        /```[\s\S]*?```/,     // Code blocks
        /^\s*[-*+]\s+/m,      // Unordered lists
        /^\s*\d+\.\s+/m,      // Ordered lists
        /^\s*>\s+/m,          // Blockquotes
        /\[.*?\]\(.*?\)/,     // Links
        /^\s*---+\s*$/m       // Horizontal rules
    ];

    return markdownPatterns.some(pattern => pattern.test(text));
} 